import { zodResolver } from "@hookform/resolvers/zod";
import { type LoginInput, LoginSchema } from "@schemas";
import { VeilLogoTile } from "@veil/ui/components/brand";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useSetupStatus } from "./setup/use-setup-status";

export function Login() {
	const { refresh } = useAuth();
	const setup = useSetupStatus();
	const form = useForm<LoginInput>({
		resolver: zodResolver(LoginSchema),
		defaultValues: { username: "", password: "" },
		mode: "onSubmit",
	});

	const onSubmit = form.handleSubmit(async (values) => {
		try {
			await unwrap(api.api.auth.login.post(values));
			await refresh();
			toast.success("Signed in");
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Login failed");
		}
	});

	const submitting = form.formState.isSubmitting;

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
			<Card className="w-full max-w-sm">
				<CardHeader>
					<div className="flex items-center gap-2.5">
						<VeilLogoTile className="size-8 rounded-md" />
						<CardTitle>Cluster Manager</CardTitle>
					</div>
					<CardDescription>
						Sign in to orchestrate VeilBrowser releases.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={onSubmit} noValidate>
						<FieldGroup>
							<Field data-invalid={!!form.formState.errors.username}>
								<FieldLabel htmlFor="username">Username</FieldLabel>
								<Input
									id="username"
									autoFocus
									autoComplete="username"
									aria-invalid={!!form.formState.errors.username}
									{...form.register("username")}
								/>
								<FieldError errors={[form.formState.errors.username]} />
							</Field>
							<Field data-invalid={!!form.formState.errors.password}>
								<FieldLabel htmlFor="password">Password</FieldLabel>
								<Input
									id="password"
									type="password"
									autoComplete="current-password"
									aria-invalid={!!form.formState.errors.password}
									{...form.register("password")}
								/>
								<FieldError errors={[form.formState.errors.password]} />
							</Field>
							<Button type="submit" disabled={submitting} className="w-full">
								{submitting && (
									<Loader2 data-icon="inline-start" className="animate-spin" />
								)}
								{submitting ? "Signing in" : "Sign in"}
							</Button>
						</FieldGroup>
					</form>
					{setup.data?.needsAdmin && (
						<p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
							First run? No admin exists yet — reload this page to launch the
							setup wizard and create one with the token from{" "}
							<code className="rounded bg-muted px-1 font-mono">
								docker logs
							</code>
							.
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
